// Simulated observers for the hover help: runs the same staircase engine the test uses
// (lib/staircase.ts, which matches the server's) on a pretend patient, so the pictures and
// numbers in the help show what the settings being edited would do.

import {
  acuityParams, contrastStaircaseParams, DEFAULT_ACUITY, DEFAULT_CONTRAST, withStaircaseDefaults,
  type AcuityConfig, type ContrastConfig,
} from "../lib/protocol";
import { mulberry32 } from "../lib/rng";
import { DoubleStaircase, InterleavedStaircases } from "../lib/staircase";

/** The pretend patient: a logistic psychometric function, 4-way guessing, 2% lapses
 *  (the same observer as the server's tests, backend/tests/sim.py). */
export const OBSERVER = { slope: 15, gamma: 0.25, lapse: 0.02 };

/** The share right a method aims at: Kaernbach its target, Levitt 0.5^(1/n), simple 50%;
 *  QUEST estimates the curve's midpoint. */
export function targetP(method: string, weighted: number, nDown: number): number {
  if (method === "weighted") return weighted;
  if (method === "levitt") return 0.5 ** (1 / nDown);
  if (method === "simple") return 0.5;
  return OBSERVER.gamma + (1 - OBSERVER.gamma - OBSERVER.lapse) / 2;
}

/** How far the curve's midpoint lies beyond the point where the patient is right a share
 *  p of the time (log units; positive: the midpoint is harder). */
export function midpointOffset(p: number): number {
  const { slope, gamma, lapse } = OBSERVER;
  return -Math.log((1 - gamma - lapse) / (p - gamma) - 1) / slope;
}

export function pCorrect(x: number, threshold: number, slope = OBSERVER.slope,
  gamma = OBSERVER.gamma, lapse = OBSERVER.lapse): number {
  return gamma + (1 - gamma - lapse) / (1 + Math.exp(-slope * (x - threshold)));
}

export interface TracePoint {
  /** 0-based trial number in the session (attention checks included). */
  i: number;
  label: string | null;          // S1..Sn; null for an attention check
  /** What was shown: log CS for contrast (higher = fainter), logMAR for acuity. */
  value: number;
  correct: boolean;
  reversal: boolean;
  catch: boolean;
}

export interface Trace {
  points: TracePoint[];
  labels: string[];
  thresholds: Record<string, number | null>;   // per staircase, same unit as value
  estimate: number | null;
  spread: number | null;
  joinAt: Record<string, number>;               // staircase trials before each joins
  catchTotal: number;
  catchCorrect: number;
  trials: number;
}

/** One simulated contrast test (interleaved staircases) for a patient whose threshold is
 *  `trueLogCs`: the contrast at which they are right as often as the staircases aim for.
 *  Values in log CS. */
export function simulateContrast(cfg0: ContrastConfig, trueLogCs: number, seed: number): Trace {
  const cfg = withStaircaseDefaults({ ...DEFAULT_CONTRAST, ...cfg0 });
  const params = contrastStaircaseParams(cfg);
  const rng = mulberry32(seed);
  const obs = mulberry32(seed ^ 0x9e3779b9);
  const st = new InterleavedStaircases(params, rng);
  const points: TracePoint[] = [];
  const p = targetP(cfg.staircase_type, cfg.weighted_target, cfg.levitt_n_down);
  const t = -(trueLogCs + midpointOffset(p));            // the curve's midpoint, log Weber
  for (let i = 0; i < 400 && !st.isComplete; i++) {
    const plan = st.planNext();
    const correct = obs() < pCorrect(plan.value, t);
    if (plan.catch) {
      st.feedCatch(correct);
      points.push({ i, label: null, value: -plan.value, correct, reversal: false, catch: true });
      continue;
    }
    const before = plan.sc.reversalCount;
    st.feedResponse(plan.sc, correct, plan.value);
    points.push({ i, label: plan.sc.label, value: -plan.value, correct, catch: false,
      reversal: plan.sc.reversalCount > before });
  }
  const thr = st.subThresholds();
  const thresholds: Record<string, number | null> = {};
  for (const [k, v] of Object.entries(thr)) thresholds[k] = v === null ? null : -v;
  const est = st.threshold();
  return {
    points, labels: st.subs.map((s) => s.label), thresholds, estimate: est === null ? null : -est,
    spread: st.spread(), joinAt: Object.fromEntries(st.joinAt), catchTotal: st.catchTotal,
    catchCorrect: st.catchCorrect, trials: points.length,
  };
}

/** One simulated acuity test (two staircases) for a patient whose acuity is `trueLogmar`
 *  (at the share right the method aims for). Attention checks at the largest letter. */
export function simulateAcuity(cfg0: AcuityConfig, trueLogmar0: number, seed: number): Trace {
  const cfg = { ...DEFAULT_ACUITY, ...cfg0 };
  const rng = mulberry32(seed);
  const obs = mulberry32(seed ^ 0x9e3779b9);
  const ds = new DoubleStaircase(acuityParams(cfg), rng);
  // larger letters are easier: the curve's midpoint is the smaller size
  const trueLogmar = trueLogmar0 - midpointOffset(targetP(cfg.method, cfg.weighted_target, cfg.levitt_n_down));
  const points: TracePoint[] = [];
  for (let i = 0; i < 400 && !ds.isComplete; i++) {
    if (cfg.catch_rate > 0 && obs() < cfg.catch_rate) {
      points.push({ i, label: null, value: cfg.max_logmar, catch: true, reversal: false,
        correct: obs() < pCorrect(cfg.max_logmar, trueLogmar) });
      continue;
    }
    const { value, sc } = ds.nextTrial();
    const correct = obs() < pCorrect(value, trueLogmar);
    const before = sc.reversalCount;
    ds.feedResponse(sc, correct, value);
    points.push({ i, label: sc.label, value, correct, catch: false, reversal: sc.reversalCount > before });
  }
  const labels = [ds.ascending.label, ds.descending.label];
  const catches = points.filter((p) => p.catch);
  return {
    points, labels,
    thresholds: { [ds.ascending.label]: ds.ascending.threshold(), [ds.descending.label]: ds.descending.threshold() },
    estimate: ds.threshold(), spread: null, joinAt: { [labels[0]!]: 0, [labels[1]!]: 0 },
    catchTotal: catches.length, catchCorrect: catches.filter((p) => p.correct).length, trials: points.length,
  };
}

export interface Summary {
  runs: number;
  meanTrials: number;
  /** SD of the estimate around the true value, across runs (same unit as the values). */
  sd: number;
  bias: number;
  /** Share of staircase trials the pretend patient got right (how easy the test feels). */
  pctCorrect: number;
}

/** Many simulated tests at one true value: how long, how precise. */
export function summarize(run: (seed: number) => Trace, truth: number, runs = 120): Summary {
  let n = 0;
  let s = 0;
  let ss = 0;
  let trials = 0;
  let right = 0;
  let main = 0;
  for (let k = 0; k < runs; k++) {
    const tr = run(1000 + k * 7919);
    trials += tr.trials;
    for (const p of tr.points) if (!p.catch) { main += 1; if (p.correct) right += 1; }
    if (tr.estimate === null) continue;
    const e = tr.estimate - truth;
    n += 1;
    s += e;
    ss += e * e;
  }
  const bias = n ? s / n : 0;
  return { runs, meanTrials: trials / runs, sd: n > 1 ? Math.sqrt((ss - n * bias * bias) / (n - 1)) : 0, bias,
    pctCorrect: main ? (100 * right) / main : 0 };
}
